import { $loggedInSettings } from '@/stores/logged-in-settings'
import dayjs, { type Dayjs } from '@/utilities/dayjs-config'
import { getDaysOfFoodFromSku, getMealsArrayFromSku } from '@/utilities/sakara'

import type { Delivery } from './delivery-zone'
import { Meal } from './meal'
import { Subscription } from './subscription'

export class MealProgramSubscription extends Subscription {
  #meals: string[] = []
  #daysOfFood?: number
  #totalPrice: number = 0 // TODO: Is it okay?
  #deliveryDays: any
  #nextSubscriptionWeek?: Dayjs
  #deliveries?: Delivery[]
  #upcomingWeekToBeBilled?: Dayjs
  #hasDinner?: boolean
  #hasLunch?: boolean
  #hasBreakfast?: boolean

  get config() {
    return $loggedInSettings.get()?.programs.signatureProgram
  }

  get meals() {
    if (this.#meals.length) return this.#meals

    this.#meals = getMealsArrayFromSku(this.sku) || []

    return this.#meals
  }

  get daysOfFood() {
    if (this.#daysOfFood) return this.#daysOfFood

    this.#daysOfFood = getDaysOfFoodFromSku(this.sku)

    return this.#daysOfFood
  }

  get addonsPrice() {
    return this.addons.reduce(
      (sum, addon) => sum + addon.pricePerUnit * addon.quantity,
      0,
    )
  }

  get totalPrice() {
    if (this.#totalPrice) return this.#totalPrice

    this.#totalPrice = this.price + this.addonsPrice

    return this.#totalPrice
  }

  get isEligibleForBlackFridayDiscount() {
    return false // TODO: implement
  }

  get nearestBlackFridayWeek() {
    return false // TODO: implement
  }

  get hasBreakfast() {
    if (this.#hasBreakfast) return this.#hasBreakfast

    this.#hasBreakfast = this.meals.includes('breakfast')

    return this.#hasBreakfast
  }

  get hasLunch() {
    if (this.#hasLunch) return this.#hasLunch

    this.#hasLunch = this.meals.includes('lunch')

    return this.#hasLunch
  }

  get hasDinner() {
    if (this.#hasDinner) return this.#hasDinner

    this.#hasDinner = this.meals.includes('dinner')

    return this.#hasDinner
  }

  get upcomingWeekToBeBilled() {
    if (this.#upcomingWeekToBeBilled) return this.#upcomingWeekToBeBilled

    this.#upcomingWeekToBeBilled = this.nextBillingDate
      .startOf('week')
      .add(1, 'week')

    return this.#upcomingWeekToBeBilled
  }

  /**
   *
   * @example
   * For a Nationwide delivery zone (4 days of food, breakfast, lunch):
   *
   * Will return:
   * [
   * 	{
   * 		"day": "Tuesday",
   * 		"mealCodes" [
   * 			"B3", "L3", "B4", "L4", "B5", "L5"
   * 		]
   * 	},
   * 	{
   * 		"day": "Friday",
   * 		"mealCodes" [
   * 			"B6", "L6"
   * 		]
   * 	}
   * ]
   */
  get deliveries(): Delivery[] {
    if (this.#deliveries) return this.#deliveries

    this.#deliveries = this.deliveryZone?.getDeliveriesForDaysOfFoodAndMeals(
      this.daysOfFood,
      this.meals,
    )

    return this.#deliveries || []
  }

  /**
   *
   * @returns Delivery days
   * @example
   * For the Inner NYC delivery zone (4 days of food):
   *
   * Will return:
   * ["Sunday", "Wednesday"]
   */
  get deliveryDays() {
    if (this.#deliveryDays) return this.#deliveryDays

    this.#deliveryDays = this.deliveries.map((d) => d.day)

    return this.#deliveryDays
  }

  get nextSubscriptionWeek() {
    if (this.#nextSubscriptionWeek) return this.#nextSubscriptionWeek

    this.#nextSubscriptionWeek = this.getNextSubscriptionWeek() || undefined

    return this.#nextSubscriptionWeek
  }

  /**
   * Whether or not the given week lies within the subscription.
   */
  isSubscriptionWeek(week: Dayjs) {
    return week.isSameOrAfter(this.startDate, 'week')
  }

  /**
   * Gets the closest week to the given week that is a part of the subscription.
   * If the given week is in the subscription, will return the current week, otherwise the
   * start date will be returned.
   */
  getNextSubscriptionWeek(week = dayjs()) {
    if (this.toBeReactivated) {
      // If the subscription is scheduled to be reactivated, return the week following the
      // reactivation date. The reactivation date is essentially when the subscription will be
      // billed, & subscriptions are billed the week before an upcoming subscription week.
      return dayjs(this.reactivationDate).add(1, 'week').startOf('week')
    }

    return dayjs.max(
      dayjs(this.startDate).startOf('week'),
      dayjs(week).startOf('week'),
    )
  }

  getMealDaysForDaysOfFood(
    daysOfFood: number,
    { dayFormat }: { dayFormat: string } = { dayFormat: 'dddd' },
  ) {
    const deliveries = this.deliveryZone?.getDeliveriesForDaysOfFoodAndMeals(
      daysOfFood,
      this.meals,
    )
    if (!deliveries) return undefined

    const mealCodes = deliveries.map((delivery) => delivery.mealCodes).flat()
    const week = 'week' in this ? (this.week as Dayjs) : undefined

    const firstMealCode = mealCodes?.[0]
    const firstMealDate = Meal.getDateFromMealCode(firstMealCode, week)
    const firstMealType = Meal.getMealTypeFromMealCode(firstMealCode)
    const firstMealDay = firstMealDate.format(dayFormat)

    const lastMealCode = mealCodes[mealCodes.length - 1]
    const lastMealDate = Meal.getDateFromMealCode(lastMealCode, week)
    const lastMealType = Meal.getMealTypeFromMealCode(lastMealCode)
    const lastMealDay = lastMealDate.format(dayFormat)

    const daySpan = lastMealDate.diff(firstMealDate, 'days') + 1

    let mealRange = `(${firstMealDay} - ${lastMealDay})`

    if (daySpan > daysOfFood) {
      mealRange = `(${firstMealDay} ${firstMealType} - ${lastMealDay} ${lastMealType})`
    }

    return mealRange
  }
}
